import React from 'react';
import { Building2, Loader2, CheckCircle, AlertCircle, Trash2 } from 'lucide-react';

const CompanyCard = ({ company, onView, onSave, onDelete, saving = false, saved = false }) => {
    // Use explicit status from parent
    const status = company.status || 'loading';
    // Support both jobData (Jobs page) and companyData (HiringSignals page)
    const dataSource = company.jobData || company.companyData || company;
    const companyName = dataSource?.companyName || dataSource?.company || dataSource?.name || company.name || 'Unknown Company';
    const location = dataSource?.jobLocation || dataSource?.location || dataSource?.hqLocation || company.location || 'Location not specified';
    const logo = dataSource?.companyLogo || company.companyLogo;
    
    // Truncate long names
    const displayName = companyName.length > 25 ? companyName.substring(0, 25) + '...' : companyName;

    const awaitingStrategy = company.analysisComplete && !company.strategyComplete;

    const statusConfig = {
        loading: {
            label: 'Loading...',
            message: 'Identifying key recruiters...',
            showButton: false,
            progress: '40%'
        },
        analyzing: {
            label: awaitingStrategy ? 'Finalizing...' : 'Analyzing...',
            message: awaitingStrategy ? 'Generating referral strategy...' : 'Running batch analysis...',
            showButton: false,
            progress: awaitingStrategy ? '95%' : '75%'
        },
        complete: {
            label: 'Complete',
            message: `Analysis complete for ${(company.recruiterData?.hiring_manager?.best_guesses?.length || 0) + (company.recruiterData?.recruiters?.best_guesses?.length || 0)} contacts`,
            showButton: true,
            progress: '100%'
        },
        failed: {
            label: 'Unable to fetch',
            message: company.errorMessage || 'We could not fetch recruiters for this company right now.',
            showButton: false,
            progress: '100%'
        }
    };

    const config = statusConfig[status] || statusConfig.loading;
    const canSave = typeof onSave === 'function';
    const canDelete = typeof onDelete === 'function';

    return (
        <div className="relative bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between gap-3">
                {/* Company Icon & Name */}
                <div className="flex items-start gap-3 flex-1 min-w-0">
                    {logo ? (
                        <img 
                            src={logo} 
                            alt={companyName}
                            className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                        />
                    ) : (
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Building2 className="text-white" size={24} />
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <h3 
                            className="font-semibold text-gray-900 truncate" 
                            title={companyName}
                        >
                            {displayName}
                        </h3>
                        <p className="text-sm text-gray-500 truncate">{location}</p>
                    </div>
                </div>

                {/* Status Indicator */}
                <div className="flex-shrink-0">
                    {config.showButton ? (
                        <div className="flex flex-col gap-2">
                            <button
                                onClick={() => onView(company)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm whitespace-nowrap"
                            >
                                View
                            </button>
                            <button
                                onClick={() => {
                                    if (!canSave) return;
                                    onSave(company);
                                }}
                                disabled={!canSave || saving || saved}
                                className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm whitespace-nowrap ${
                                    saved
                                        ? 'bg-green-600 text-white cursor-not-allowed'
                                        : saving
                                            ? 'bg-gray-400 text-white cursor-wait'
                                            : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                            >
                                {saved ? 'Saved' : saving ? 'Saving...' : canSave ? 'Save Job' : 'Save unavailable'}
                            </button>
                        </div>
                    ) : status === 'failed' ? (
                        <div className="flex flex-col items-center gap-1">
                            <AlertCircle className="text-amber-600" size={20} />
                            <span className="text-xs text-gray-500 whitespace-nowrap">Unable to fetch</span>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center gap-1">
                            <Loader2 className="text-blue-600 animate-spin" size={20} />
                            <span className="text-xs text-gray-500 whitespace-nowrap">{config.label}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Status Message & Progress */}
            <div className="mt-3">
                {status === 'complete' ? (
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 text-xs text-green-600">
                            <CheckCircle size={14} />
                            <span>{config.message}</span>
                        </div>
                        {canDelete && (
                            <button
                                onClick={() => onDelete(company)}
                                className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                                aria-label={`Delete referral analysis for ${companyName}`}
                                title="Delete referral analysis"
                            >
                                <Trash2 size={14} />
                            </button>
                        )}
                    </div>
                ) : status === 'failed' ? (
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 text-xs text-amber-700">
                            <AlertCircle size={14} />
                            <span>{config.message}</span>
                        </div>
                        {canDelete && (
                            <button
                                onClick={() => onDelete(company)}
                                className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                                aria-label={`Delete referral analysis for ${companyName}`}
                                title="Delete referral analysis"
                            >
                                <Trash2 size={14} />
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                            <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                            <span className="truncate">{config.message}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div 
                                className="bg-blue-600 h-1.5 rounded-full transition-all duration-500" 
                                style={{ width: config.progress }}
                            />
                        </div>
                        {canDelete && (
                            <div className="flex justify-end">
                                <button
                                    onClick={() => onDelete(company)}
                                    className="inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                                    aria-label={`Delete referral analysis for ${companyName}`}
                                    title="Delete referral analysis"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CompanyCard;
